/**
 * Admin MFA verification — a single field that accepts either a 6-digit TOTP code OR a recovery
 * code (both route through useAdminSession.verifyMfa). On success the store flips to 'authenticated'
 * and the AdminAuthGate replaces to /admin.
 */
import { useState } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { useAdminSession } from '../../src/auth/adminSession';
import { AdminApiError } from '../../src/api/adminClient';
import { Screen, Text, Field, Button } from '../../src/components/ui';
import { theme } from '../../src/theme';

export default function AdminVerifyMfaScreen() {
  const verifyMfa = useAdminSession((s) => s.verifyMfa);

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleVerify() {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter your authentication code.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await verifyMfa(trimmed);
      // Gate handles navigation once status → 'authenticated'.
    } catch (err) {
      if (err instanceof AdminApiError || err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Screen maxWidth={420}>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={styles.container}>
          <View style={styles.brand}>
            <Text variant="title">Two-factor authentication</Text>
            <Text variant="subhead" color="secondary">
              Enter the 6-digit code from your authenticator app, or a recovery code.
            </Text>
          </View>

          <View style={styles.form}>
            <Field
              label="Authentication code"
              value={code}
              onChangeText={setCode}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="number-pad"
              textContentType="oneTimeCode"
              placeholder="123456"
              editable={!loading}
              accessibilityLabel="Authentication or recovery code"
              returnKeyType="done"
              onSubmitEditing={handleVerify}
              error={error ?? undefined}
            />

            <Button
              title="Verify"
              onPress={handleVerify}
              loading={loading}
              disabled={loading}
              size="lg"
              style={styles.submit}
            />
            <Text variant="small" color="secondary">
              Lost your device? Enter one of your saved recovery codes above.
            </Text>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, justifyContent: 'center', gap: theme.spacing['4xl'] },
  brand: { alignItems: 'center', gap: theme.spacing.md },
  form: { gap: theme.spacing.md },
  submit: { marginTop: theme.spacing.sm },
});
